import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import AdminLayout from './components/layout/AdminLayout';
import LoginPage from './pages/admin/LoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import RequireCan from './components/admin/RequireCan';
import './styles/global.css';

// Lazily load administrative pages to optimize initial bundle size & load speed
const SubmissionsPage      = lazy(() => import('./pages/admin/SubmissionsPage'));
const SubmissionDetailPage = lazy(() => import('./pages/admin/SubmissionDetailPage'));
const UsersPage            = lazy(() => import('./pages/admin/UsersPage'));
const ActivityPage         = lazy(() => import('./pages/admin/ActivityPage'));
const ProfilePage          = lazy(() => import('./pages/admin/ProfilePage'));
const InvoicePage          = lazy(() => import('./pages/admin/InvoicePage'));
const InvoiceSettingsPage  = lazy(() => import('./pages/admin/InvoiceSettingsPage'));
const ClientsPage          = lazy(() => import('./pages/admin/ClientsPage'));
const ClientDetailPage     = lazy(() => import('./pages/admin/ClientDetailPage'));
const OpenServicesPage     = lazy(() => import('./pages/admin/OpenServicesPage'));
const FreelancersPage      = lazy(() => import('./pages/admin/FreelancersPage'));
const FreelancerBillsPage  = lazy(() => import('./pages/admin/FreelancerBillsPage'));
const FreelancerPaymentsPage = lazy(() => import('./pages/admin/FreelancerPaymentsPage'));
const EmployeesPage        = lazy(() => import('./pages/admin/EmployeesPage'));
const PayrollPage          = lazy(() => import('./pages/admin/PayrollPage'));
const PayslipsPage         = lazy(() => import('./pages/admin/PayslipsPage'));
const ReportsPage          = lazy(() => import('./pages/admin/ReportsPage'));
const ExpensePage          = lazy(() => import('./pages/admin/ExpensePage'));
const AdminBlogPage        = lazy(() => import('./pages/admin/BlogPage'));
const BlogEditorPage       = lazy(() => import('./pages/admin/BlogEditorPage'));
const BlogCategoriesPage   = lazy(() => import('./pages/admin/BlogCategoriesPage'));
const BlogStatusPage       = lazy(() => import('./pages/admin/BlogStatusPage'));
const LeadershipPage       = lazy(() => import('./pages/admin/LeadershipPage'));
const WorkPage             = lazy(() => import('./pages/admin/WorkPage'));
const WorkCategoriesPage   = lazy(() => import('./pages/admin/WorkCategoriesPage'));
const NewsletterPage       = lazy(() => import('./pages/admin/NewsletterPage'));
const NewsletterSendPage   = lazy(() => import('./pages/admin/NewsletterSendPage'));
const AccessDeniedPage     = lazy(() => import('./pages/admin/AccessDeniedPage'));
const NotFoundPage         = lazy(() => import('./pages/NotFoundPage'));

const AdminPageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--admin-bg)' }}>
    <div className="spinner" style={{ width: 28, height: 28 }} />
  </div>
);

const router = createBrowserRouter(
  [
    { path: '/login', element: <LoginPage /> },
    {
      path: '/',
      element: <AdminLayout />,
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: 'access-denied', element: <AccessDeniedPage /> },
        { path: 'dashboard', element: <DashboardPage /> },
        {
          path: 'submissions',
          element: (
            <RequireCan allow={['submissions.view', 'super_admin', 'admin', 'agent']}>
              <SubmissionsPage />
            </RequireCan>
          ),
        },
        {
          path: 'submissions/:id',
          element: (
            <RequireCan allow={['submissions.view', 'super_admin', 'admin', 'agent']}>
              <SubmissionDetailPage />
            </RequireCan>
          ),
        },
        {
          path: 'clients',
          element: (
            <RequireCan allow={['clients.view', 'super_admin', 'admin', 'agent']}>
              <ClientsPage />
            </RequireCan>
          ),
        },
        {
          path: 'clients/:id',
          element: (
            <RequireCan allow={['clients.view', 'super_admin', 'admin', 'agent']}>
              <ClientDetailPage />
            </RequireCan>
          ),
        },
        {
          path: 'open-services',
          element: (
            <RequireCan allow={['clients.view', 'super_admin', 'admin', 'agent']}>
              <OpenServicesPage />
            </RequireCan>
          ),
        },
        { path: 'freelancer', element: <Navigate to="/freelancers" replace /> },
        {
          path: 'freelancers',
          element: (
            <RequireCan allow={['clients.view', 'super_admin', 'admin', 'agent']}>
              <FreelancersPage />
            </RequireCan>
          ),
        },
        {
          path: 'freelancers/bills',
          element: (
            <RequireCan allow={['clients.view', 'super_admin', 'admin', 'agent']}>
              <FreelancerBillsPage />
            </RequireCan>
          ),
        },
        {
          path: 'freelancers/payments',
          element: (
            <RequireCan allow={['clients.view', 'super_admin', 'admin', 'agent']}>
              <FreelancerPaymentsPage />
            </RequireCan>
          ),
        },
        // Employee singular & plural route handlers
        { path: 'employee', element: <Navigate to="/employees" replace /> },
        { path: 'employee/payroll', element: <Navigate to="/employees/payroll" replace /> },
        { path: 'employee/payslips', element: <Navigate to="/employees/payslips" replace /> },
        { path: 'payroll', element: <Navigate to="/employees/payroll" replace /> },
        { path: 'payslips', element: <Navigate to="/employees/payslips" replace /> },
        {
          path: 'employees',
          element: (
            <RequireCan allow={['clients.view', 'financial.view', 'super_admin', 'admin', 'agent']}>
              <EmployeesPage />
            </RequireCan>
          ),
        },
        {
          path: 'employees/payroll',
          element: (
            <RequireCan allow={['financial.view', 'super_admin', 'admin', 'agent']}>
              <PayrollPage />
            </RequireCan>
          ),
        },
        {
          path: 'employees/payslips',
          element: (
            <RequireCan allow={['financial.view', 'super_admin', 'admin', 'agent']}>
              <PayslipsPage />
            </RequireCan>
          ),
        },
        {
          path: 'reports',
          element: (
            <RequireCan allow={['financial.view', 'super_admin', 'admin', 'agent']}>
              <ReportsPage />
            </RequireCan>
          ),
        },
        {
          path: 'users',
          element: (
            <RequireCan allow={['users.view', 'super_admin', 'admin']}>
              <UsersPage />
            </RequireCan>
          ),
        },
        {
          path: 'activity',
          element: (
            <RequireCan allow={['activity.view', 'super_admin', 'admin', 'agent']}>
              <ActivityPage />
            </RequireCan>
          ),
        },
        {
          path: 'invoice',
          element: (
            <RequireCan allow={['invoices.view', 'super_admin', 'admin', 'agent']}>
              <InvoicePage />
            </RequireCan>
          ),
        },
        {
          path: 'invoice/track',
          element: (
            <RequireCan allow={['invoices.view', 'super_admin', 'admin', 'agent']}>
              <InvoicePage />
            </RequireCan>
          ),
        },
        {
          path: 'invoice/settings',
          element: (
            <RequireCan allow={['invoices.settings', 'super_admin', 'admin']}>
              <InvoiceSettingsPage />
            </RequireCan>
          ),
        },
        {
          path: 'financial',
          element: (
            <RequireCan allow={['financial.view', 'super_admin', 'admin', 'agent']}>
              <ReportsPage />
            </RequireCan>
          ),
        },
        {
          path: 'expenses',
          element: (
            <RequireCan allow={['expenses.view', 'super_admin', 'admin', 'agent']}>
              <ExpensePage />
            </RequireCan>
          ),
        },
        { path: 'expense-categories', element: <Navigate to="/expenses" replace /> },
        {
          path: 'blog',
          element: (
            <RequireCan allow={['blog.view', 'super_admin', 'admin', 'agent']}>
              <AdminBlogPage />
            </RequireCan>
          ),
        },
        {
          path: 'blog/status',
          element: (
            <RequireCan allow={['blog.view', 'super_admin', 'admin', 'agent']}>
              <BlogStatusPage />
            </RequireCan>
          ),
        },
        {
          path: 'blog/create',
          element: (
            <RequireCan allow={['blog.create', 'super_admin', 'admin', 'agent']}>
              <BlogEditorPage />
            </RequireCan>
          ),
        },
        {
          path: 'blog/categories',
          element: (
            <RequireCan allow={['blog.categories', 'super_admin', 'admin']}>
              <BlogCategoriesPage />
            </RequireCan>
          ),
        },
        {
          path: 'blog/:id/edit',
          element: (
            <RequireCan allow={['blog.edit', 'super_admin', 'admin', 'agent']}>
              <BlogEditorPage />
            </RequireCan>
          ),
        },
        {
          path: 'work',
          element: (
            <RequireCan allow={['super_admin', 'admin', 'agent']}>
              <WorkPage />
            </RequireCan>
          ),
        },
        {
          path: 'work/categories',
          element: (
            <RequireCan allow={['super_admin', 'admin']}>
              <WorkCategoriesPage />
            </RequireCan>
          ),
        },
        {
          path: 'leadership',
          element: (
            <RequireCan allow={['leadership.view', 'leadership.manage', 'super_admin', 'admin', 'agent']}>
              <LeadershipPage />
            </RequireCan>
          ),
        },
        {
          path: 'newsletter',
          element: (
            <RequireCan allow={['newsletter.view', 'super_admin', 'admin']}>
              <NewsletterPage />
            </RequireCan>
          ),
        },
        {
          path: 'newsletter/send',
          element: (
            <RequireCan allow={['newsletter.send', 'super_admin', 'admin']}>
              <NewsletterSendPage />
            </RequireCan>
          ),
        },
        { path: 'profile', element: <ProfilePage /> },
      ],
    },
    { path: '*', element: <NotFoundPage /> },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);

function App() {
  return (
    <AuthProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { background: '#363636', color: '#fff' },
          success: { duration: 3000, iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { duration: 5000, iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      />
      <Suspense fallback={<AdminPageLoader />}>
        <RouterProvider router={router} />
      </Suspense>
    </AuthProvider>
  );
}

export default App;